import { Injectable, inject } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { firstValueFrom } from 'rxjs';

import type {
  BuscaPessoaModalResultQualificacao,
  IdentificarPessoaModalResultQualificacao,
  CadastroPessoaWizardDataQualificacao,
  CadastroPessoaWizardResultQualificacao,
  PessoaConsultaParaQualificacao,
} from '../../../../shared/models/qualificacao';
import type { QualificacaoPessoaService } from '../../../../core/qualificacao-pessoa/qualificacao-pessoa.interface';
import { AuditService } from '../../../../core/telemetry-logging/services/audit.service';
import { AuditEventType, AuditResult } from '../../../../core/telemetry-logging/models/audit-event.model';

import { BuscaPessoaModal } from '../components/busca-pessoa-modal/busca-pessoa-modal';
import { IdentificarPessoaModal } from '../components/identificar-pessoa-modal/identificar-pessoa-modal';
import { CadastroPessoaWizard } from '../components/cadastro-pessoa-wizard/cadastro-pessoa-wizard';
import { PessoaDetalhe } from '../models';
import { PessoaStore } from '../data-access/pessoa.store';
import { PessoaDataAccessService } from '../data-access/pessoa-data-access.service';
import { mapPessoaToPessoaDetalhe } from '../data-access/pessoa-mapper';
import { DIALOG_PRESETS } from '../../../../shared/utils/dialog-presets';

/**
 * Implementação do QualificacaoPessoaService.
 * Abre os modais de consultas e retorna resultados tipados para shared.
 */
@Injectable({ providedIn: 'root' })
export class QualificacaoPessoaServiceImpl implements QualificacaoPessoaService {
  private readonly dialog = inject(MatDialog);
  private readonly pessoaStore = inject(PessoaStore);
  private readonly pessoaDataAccess = inject(PessoaDataAccessService);
  private readonly audit = inject(AuditService);

  async abrirBuscaPorNome(
    permitirCadastroManual = true
  ): Promise<BuscaPessoaModalResultQualificacao> {
    const dialogRef = this.dialog.open(BuscaPessoaModal, {
      ...DIALOG_PRESETS.formCompact,
      data: { permitirCadastroManual },
    });

    const result = await firstValueFrom(dialogRef.afterClosed());
    this.pessoaStore.limparTudo();
    return (result ?? { cancelado: true }) as BuscaPessoaModalResultQualificacao;
  }

  async abrirBuscaPorCPF(
    permitirCadastroManual = true
  ): Promise<IdentificarPessoaModalResultQualificacao> {
    const pessoaPlaceholder: PessoaDetalhe = {
      idInterno: 'temp-' + Date.now(),
      idPesquisa: '',
      nomeCompleto: 'Busca por CPF',
      cpf: '',
      dadosValidados: false,
      alertasCriticos: [],
      alcunhas: [],
      enderecos: [],
      telefones: [],
      emails: [],
      fotos: [],
    };

    const dialogRef = this.dialog.open(IdentificarPessoaModal, {
      data: {
        pessoaOriginal: pessoaPlaceholder,
        permitirCadastroManual,
        contexto: 'rap',
        apenasConsultaCpf: true,
      },
      width: '500px',
      maxHeight: '90vh',
      disableClose: false,
    });

    const result = await firstValueFrom(dialogRef.afterClosed());
    this.pessoaStore.limparTudo();
    return result as IdentificarPessoaModalResultQualificacao;
  }

  async abrirEditarPessoa(
    data: CadastroPessoaWizardDataQualificacao
  ): Promise<CadastroPessoaWizardResultQualificacao> {
    const wizardRef = this.dialog.open(CadastroPessoaWizard, {
      data,
      width: '95vw',
      maxWidth: '500px',
      height: '90vh',
      maxHeight: '700px',
      disableClose: true,
      panelClass: 'cadastro-pessoa-wizard-dialog',
    });

    const result = await firstValueFrom(wizardRef.afterClosed());
    return (result ?? { sucesso: false }) as CadastroPessoaWizardResultQualificacao;
  }

  async consultarPorCpfDireto(cpf: string): Promise<PessoaConsultaParaQualificacao | null> {
    try {
      const pessoa = await firstValueFrom(this.pessoaDataAccess.consultarPorCPF(cpf));
      const pessoaDetalhe: PessoaDetalhe = mapPessoaToPessoaDetalhe(pessoa);

      this.registrarAuditoriaConsultaDireta(cpf, AuditResult.SUCCESS);

      return this.paraQualificacao(pessoaDetalhe);
    } catch (erro: any) {
      const naoEncontrado = erro?.status === 404;
      this.registrarAuditoriaConsultaDireta(
        cpf,
        naoEncontrado ? AuditResult.PARTIAL : AuditResult.FAILURE,
        naoEncontrado ? undefined : (erro?.error?.message || erro?.message || 'Erro ao consultar pessoa')
      );
      return null;
    }
  }

  private paraQualificacao(pessoa: PessoaDetalhe): PessoaConsultaParaQualificacao {
    return {
      idInterno: pessoa.idInterno,
      idPessoaNexus: pessoa.idPessoaNexus,
      nomeCompleto: pessoa.nomeCompleto,
      cpf: pessoa.cpf,
      dataNascimento: pessoa.dataNascimento,
      idade: pessoa.idade,
      nomeMae: pessoa.nomeMae,
      rg: pessoa.rg,
      fotoPrincipalUrl: pessoa.fotoPrincipalUrl,
    };
  }

  /**
   * Auditoria da consulta silenciosa por CPF (ADR-0002).
   * Não usa `PessoaStore.registrarAuditoriaConsulta` pois esse método é privado
   * e acoplado ao fluxo de `consultarPessoa()` — a consulta direta não altera o store.
   */
  private registrarAuditoriaConsultaDireta(
    cpf: string,
    resultado: AuditResult,
    erroResumo?: string
  ): void {
    const digitos = cpf.replace(/\D/g, '');
    this.audit.log({
      eventType: AuditEventType.CONSULTA_PESSOA,
      dominio: 'consultas/pessoa',
      contexto: {
        tipoConsulta: 'CPF_DIRETO',
        cpfParcial: `***.***.***-${digitos.slice(-2)}`,
      },
      resultado,
      erroResumo,
    }).subscribe();
  }
}
